import { useQuery } from "@tanstack/react-query";
import { CalendarCheck, CalendarDays, Gauge, Coins, Clock, User } from "lucide-react";
import { get } from "@/lib/api";
import { BOOKING_STATUS, formatDate } from "@/lib/data";

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-[#64748B]">{label}</span>
        <Icon className="h-4 w-4 text-[#94A3B8]" />
      </div>
      <p className="mt-3 text-3xl font-semibold tracking-tight">{value}</p>
      {hint && <p className="mt-1 text-xs text-[#94A3B8]">{hint}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { data } = useQuery({ queryKey: ["partner-dashboard"], queryFn: () => get("/partner/dashboard") });
  if (!data) return <p className="text-[#64748B]">Yükleniyor…</p>;

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight">Ana Panel</h1>
        <p className="text-sm text-[#64748B]">{data.studio?.name} · {data.studio?.region}</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={CalendarCheck} label="Bugünkü rezervasyonlar" value={data.today_bookings_count} />
        <Stat icon={CalendarDays} label="Bugünkü seanslar" value={data.today_sessions_count} />
        <Stat icon={Gauge} label="Ortalama doluluk" value={`%${data.avg_occupancy}`} />
        <Stat icon={Coins} label="Tahmini hakediş" value={`${data.estimated_earnings}`} hint="kredi cinsinden" />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <div className="rounded-md border border-[#E2E8F0] bg-white">
          <div className="border-b border-[#E2E8F0] px-5 py-4"><h2 className="font-semibold">Son rezervasyonlar</h2></div>
          <div className="divide-y divide-[#F1F5F9]">
            {data.recent_bookings.length === 0 && <p className="p-5 text-sm text-[#64748B]">Kayıt yok.</p>}
            {data.recent_bookings.map((b) => {
              const st = BOOKING_STATUS[b.status];
              return (
                <div key={b.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <p className="text-sm font-medium">{b.user_name}</p>
                    <p className="text-xs text-[#64748B]">{b.session_title} · {formatDate(b.date)} {b.start_time}</p>
                  </div>
                  <span className={`rounded border px-2 py-0.5 text-xs font-medium ${st.color}`}>{st.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        <div className="rounded-md border border-[#E2E8F0] bg-white">
          <div className="border-b border-[#E2E8F0] px-5 py-4"><h2 className="font-semibold">Yaklaşan seanslar</h2></div>
          <div className="divide-y divide-[#F1F5F9]">
            {data.upcoming_sessions.map((s) => (
              <div key={s.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium">{s.title}</p>
                  <p className="flex items-center gap-1 text-xs text-[#64748B]"><User className="h-3 w-3" /> {s.instructor}</p>
                </div>
                <div className="text-right">
                  <p className="flex items-center gap-1 text-sm font-medium"><Clock className="h-3 w-3" /> {formatDate(s.date)} {s.start_time}</p>
                  <p className="text-xs text-[#64748B]">{s.booked_count}/{s.capacity} dolu</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
